// Google Analytics and Google Ads tracking utility

import {
  isBodexDkHostname,
  getSpanishAliasSourceDomain,
  getValuationMarket as getValuationMarketForLocation,
  isBodexEsHostname,
  isBodexNoHostname,
  isSpanishMarketVisit
} from './spanishMarket';

/**
 * True on the Spain-market variants: bodex.es, bodex.se/spanien,
 * bodex.no/spania, and bodex.dk/spanien. Used for EUR prices and Spain
 * autocomplete.
 */
export const isBodexEsHost = () => {
  if (typeof window === 'undefined') return false;
  try {
    return isSpanishMarketVisit(window.location);
  } catch (_err) {
    return false;
  }
};

export const getValuationMarket = () => {
  if (typeof window === 'undefined') return 'SE';
  try {
    return getValuationMarketForLocation(window.location);
  } catch (_err) {
    return 'SE';
  }
};

/**
 * Returns the conversion form type to use for valuation chat input events.
 * Sends `valuation_input_in_form_es` on the Spanish market; otherwise the
 * default `valuation_input_in_form`.
 */
export const getValuationInputFormType = () => (
  isBodexEsHost() ? 'valuation_input_in_form_es' : 'valuation_input_in_form'
);

/**
 * Returns the market source domain (e.g. "bodex.se", "www.bodex.es", "bodex.no").
 * Sent to the backend so newly created Lead rows record which Bodex site the
 * visitor came from. The backend then routes the offline Google Ads
 * conversion (mark-correct / grade-lead) to the matching conversion action.
 * The explicit valuation_market field decides whether the Nordic hosts use
 * their home market or their Spain route.
 *
 * Returns "" on SSR / when window.location is unavailable so callers can pass
 * the value through unconditionally.
 */
export const getSourceDomain = () => {
  if (typeof window === 'undefined') return '';
  try {
    const hostname = (window.location.hostname || '').toLowerCase();
    if (isBodexNoHostname(hostname) || isBodexDkHostname(hostname)) {
      return hostname;
    }
    return isSpanishMarketVisit(window.location) && !isBodexEsHostname(hostname)
      ? getSpanishAliasSourceDomain()
      : hostname;
  } catch (_err) {
    return '';
  }
};

/**
 * A generic event tracking function for Google Analytics.
 * This can be used for tracking custom events.
 * 
 * @param {string} action - The event action (e.g., 'login', 'click').
 * @param {string} category - The event category (e.g., 'engagement', 'form').
 * @param {string} label - The event label (e.g., 'Login Button', 'Main Form').
 * @param {number} value - An optional numeric value for the event.
 */
export const trackEvent = (action, category, label, value) => {
  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', action, {
        'event_category': category,
        'event_label': label,
        'value': value,
      });
      console.log(`GA Event tracked: ${action} - ${category} - ${label}`);
    } catch (error) {
      console.error('Error tracking GA event:', error);
    }
  } else {
    console.warn('Google Analytics (gtag) is not available for event tracking');
  }
};

/**
 * Tracks page views for Single Page Applications.
 * This should be called on every route change.
 * 
 * @param {string} path - The path of the page being viewed (e.g., '/login').
 * @param {string} title - The title of the page.
 */
export const trackPageView = (path, title) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // The `config` command in `App.js` handles which GA property to send to.
    try {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title,
        page_location: window.location.href
      });
      console.log(`Page view tracked for: ${path}`);
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  } else {
    console.warn('Google Analytics (gtag) is not available for page view tracking');
  }
};


// Track different types of form submissions with additional data
export const trackFormSubmission = (formType, additionalData = {}, callback) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // --- Google Ads Conversion Tracking (Existing Functionality) ---
    // This part remains unchanged to ensure Google Ads conversions are still tracked.
    const CONVERSION_IDS = {
      'final_followup_form': 'AW-17166076911/C_SpCJ_27NQaEO-Xtvk_',
      'initial_object_form': 'AW-17166076911/2v4aCIPjp9kaEO-Xtvk_',
      'intermediate_followup_form': 'AW-17166076911/C_SpCJ_27NQaEO-Xtvk_',
      'login_success': 'AW-17166076911/637UCLaX9dUaEO-Xtvk_',
      'ClickToObjectForm_Header': 'AW-17166076911/zByvCJOB4NgaEO-Xtvk_',
      'ClickToObjectForm_Hero': 'AW-17166076911/7K5KCN-VldkaEO-Xtvk_',
      'ClickToObjectForm_LoginSection': 'AW-17166076911/v0ziCLKUp9kaEO-Xtvk_',
      'valuation_button_homepage': 'AW-17166076911/Yq0jCOGd4toaEO-Xtvk_',
      'valuation_create_ad_click': 'AW-17166076911/YF1SCJfAuOAaEO-Xtvk_',
      'valuation_svara_click': 'AW-17166076911/Xn4PCO3nztoaEO-Xtvk_',
      'valuation_input_in_form': 'AW-17166076911/mOuBCPq1n-cbEO-Xtvk_',
      'valuation_input_in_form_es': 'AW-17166076911/4-b_CJWc_aIcEO-Xtvk_',
      'default': 'AW-17166076911/C_SpCJ_27NQaEO-Xtvk_'
    };

    const sendToId = CONVERSION_IDS[formType] || CONVERSION_IDS['default'];

    // Keep the Spanish-market valuation input conversion at 1 SEK without
    // changing the existing Swedish valuation_input_in_form value handling.
    const conversionValue = formType === 'valuation_input_in_form_es'
      ? 1.0
      : (additionalData.conversion_value || 1.0);

    const eventPayload = {
        'send_to': sendToId,
        'value': conversionValue,
        'currency': 'SEK',
        'transaction_id': '', 
        ...additionalData
    };

    if (callback) {
        eventPayload.event_callback = callback;
    }

    try {
      window.gtag('event', 'conversion', eventPayload);
      console.log(`Google Ads conversion tracked for ${formType} to ${sendToId}`, additionalData);
    } catch (error) {
      console.error('Error tracking Google Ads conversion:', error);
      if (callback) {
        callback();
      }
    }

    // --- Google Analytics Event Tracking (New Functionality) ---
    // We also send a separate, more detailed event to Google Analytics.
    try {
      window.gtag('event', 'form_submission', {
        'event_category': 'Form',
        'event_label': formType,
        'form_type': formType,
        ...additionalData
      });
      console.log(`GA event tracked for form_submission: ${formType}`, additionalData);
    } catch (error) {
      console.error('Error tracking GA form submission event:', error);
    }

  } else {
    console.warn('Google Analytics (gtag) is not available for conversion tracking');
    if (callback) {
        callback();
    }
  }
};
